import React, { useEffect } from 'react';
import { message } from 'antd';
import { useSearchParams } from 'react-router-dom';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { EmptyState, TWButton, TWDataTable } from '../../../components/ui';
import { DataTableSkeleton, colTitle } from '../../../data/columns';
import fleetApi from '../../../services/fleetApi';
import useFleetPoll, { fleetErrorText } from '../use-fleet-poll';
import { PAGE_SIZE } from '../fleet-utils';
import { absoluteTime, pageOf, truncate, writePage } from './device-tab-utils';

const DETAIL_MAX = 60;
const DASH = '—';

const SKELETON_HEADERS = [
  { label: 'When', w: 'w-28' },
  { label: 'Who', w: 'w-40' },
  { label: 'Action', w: 'w-32' },
  { label: 'Detail', w: 'w-48' },
  { label: 'IP', w: 'w-24' },
];

const detailText = (detail) => {
  if (detail === null || detail === undefined) return '';
  return JSON.stringify(detail) ?? '';
};

const columns = [
  {
    title: colTitle('When'),
    key: 'when',
    render: (_, row) => (
      <span className="whitespace-nowrap text-sm text-slate-600 dark:text-slate-300">
        {absoluteTime(row.createdate)}
      </span>
    ),
  },
  {
    title: colTitle('Who'),
    key: 'who',
    render: (_, row) => (
      <span className="text-sm text-slate-600 dark:text-slate-300">{row.actoremail || 'system'}</span>
    ),
  },
  {
    title: colTitle('Action'),
    key: 'action',
    render: (_, row) => (
      <span className="text-sm font-medium text-slate-900 dark:text-slate-100">{row.action}</span>
    ),
  },
  {
    title: colTitle('Detail'),
    key: 'detail',
    render: (_, row) => {
      const full = detailText(row.detail);
      return full ? (
        <span title={full} className="font-mono text-xs text-slate-600 dark:text-slate-300">
          {truncate(full, DETAIL_MAX)}
        </span>
      ) : (
        DASH
      );
    },
  },
  {
    title: colTitle('IP'),
    key: 'ip',
    render: (_, row) => (
      <span className="text-sm text-slate-600 dark:text-slate-300">{row.ip || DASH}</span>
    ),
  },
];

// Every audit row of this device. Detail is rendered as text only.
const DeviceAuditTab = ({ device }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const page = pageOf(searchParams);
  const query = `?deviceid=${device.id}&pageNumber=${page}&pageSize=${PAGE_SIZE}`;

  const { data, loading, fetching, error, reload } = useFleetPoll(
    () => fleetApi.listAudit(query).then((res) => res.data),
    {
      deps: [query],
      onRefetchError: (err) => message.error(fleetErrorText(err, 'Could not load audit log')),
    },
  );

  const list = data?.data || [];
  const totalRecords = data?.totalRecords ?? 0;

  // A page past the end answers [] with a positive count: go back to page 1.
  const pastTheEnd = !loading && !error && list.length === 0 && totalRecords > 0 && page > 1;
  useEffect(() => {
    if (pastTheEnd) writePage(setSearchParams, 1);
  }, [pastTheEnd, setSearchParams]);

  if (error) {
    return (
      <EmptyState
        icon={ExclamationTriangleIcon}
        title="Could not load audit log"
        description={fleetErrorText(error)}
        action={<TWButton onClick={reload}>Retry</TWButton>}
      />
    );
  }
  if (loading || pastTheEnd) return <DataTableSkeleton headers={SKELETON_HEADERS} rows={8} />;
  if (totalRecords === 0) {
    return (
      <EmptyState title="No activity yet" description="Changes to this device are recorded here." />
    );
  }
  return (
    <TWDataTable
      columns={columns}
      data={list}
      loading={fetching}
      pagination={{
        current: page,
        pageSize: PAGE_SIZE,
        total: totalRecords,
        showSizeChanger: false,
        showTotal: (t) => `${t} ${t === 1 ? 'entry' : 'entries'}`,
        onChange: (next) => writePage(setSearchParams, next),
      }}
    />
  );
};

export default DeviceAuditTab;
